
import Vue from 'vue';
import { Swiper, SwiperSlide } from 'vue-awesome-swiper';
import { Loading, Tab, Tabs } from '@descomplica/vue-components';
import {
  AppointmentModel,
  BoardSectionModel,
  ClassroomModel,
  CourseModel,
  EnrollmentModel,
  ListModel,
  UserModel,
} from '@descomplica/dex-models';
import ListCard from '@/components/cards/ListCard.vue';
import ClassroomHeader from '@/components/headers/ClassroomHeader.vue';
import LiveLessonList from '@/components/LiveLessonList.vue';
import RevisionCard from '@/components/cards/RevisionCard.vue';
import Notebook from '@/views/Notebook.vue';
import RevisionCardSkeleton from '@/components/skeletons/RevisionCardSkeleton.vue';
import { capitalizeAllWords } from '@/helpers/string';
import { checkUserEnrollmentAndTaskByPeriodStart } from '@/helpers/users';

export default Vue.extend({
  components: {
    Tab,
    Tabs,
    ListCard,
    ClassroomHeader,
    LiveLessonList,
    Loading,
    RevisionCard,
    Swiper,
    SwiperSlide,
    RevisionCardSkeleton,
    Notebook,
  },
  data() {
    return {
      swiperOption: {
        pagination: {
          el: '.swiper-pagination',
          clickable: true,
        },
      },
    };
  },
  computed: {
    user(): UserModel {
      return this.$store.getters.currentUser;
    },
    isLoadingClassroomData(): boolean {
      return this.$store.getters.isLoadingClassroomData;
    },
    isLoadingRevisionLists(): boolean {
      const { descomplicaCategorySlug } = this.currentCourse;
      return descomplicaCategorySlug === 'pos'
        ? false
        : this.$store.getters.isLoadingRevisionLists(this.classroom.id) &&
            !this.revisionLists.length;
    },
    currentCourse(): CourseModel {
      return this.$store.getters.currentCourse;
    },
    isLoadingClassroomMaterials(): boolean {
      return this.$store.getters.isLoadingClassroomMaterials(this.classroom.id);
    },
    classroom(): ClassroomModel {
      return this.$store.getters.classroomBySlug(this.$route.params.classroomSlug);
    },
    currentPeriodStartDate(): string {
      return this.$store.getters.currentPeriodStartDate;
    },
    lists(): BoardSectionModel[] | Object[] {
      if (!this.isLoadingClassroomData && !this.isLoadingClassroomMaterials) {
        return this.classroom.getLists();
      }
      return [{}, {}, {}, {}];
    },
    revisionLists(): ListModel[] {
      const { revisionLists }: { revisionLists: ListModel[] } = this.classroom;
      const { classroomEnrollment }: { classroomEnrollment: EnrollmentModel | null } = this;

      if (!classroomEnrollment || !revisionLists) return [];

      const enrollmentDate: string = classroomEnrollment.createdAt;
      const periodStartDate: string = this.currentPeriodStartDate;

      return revisionLists.filter((list: ListModel) => {
        const listCreationDate = list.createdAt || new Date().toISOString();

        return checkUserEnrollmentAndTaskByPeriodStart(
          periodStartDate,
          enrollmentDate,
          listCreationDate,
        );
      });
    },
    appointments(): AppointmentModel[] {
      return this.$store.getters.filteredAppointmentByClassroomId(this.classroom.id);
    },
    classroomEnrollment(): EnrollmentModel | null {
      const startedEnrollments: EnrollmentModel[] = this.user.getStartedEnrollments() || [];
      return (
        startedEnrollments.find(
          (enrollment) => enrollment.classroomByClassroomId === this.classroom.id,
        ) || null
      );
    },
  },
  async created() {
    const { descomplicaCategorySlug } = this.currentCourse;
    const { classroomSlug } = this.$route.params;
    const userId = this.user.id;
    const hasClassroom = await this.$store.dispatch('PULL_CLASSROOM_INFO', {
      userId,
      classroomSlug,
      courseCategory: descomplicaCategorySlug,
    });
    this.handleIfUserCanSeeClassroom(hasClassroom);
    this.changePageTitle(hasClassroom);
  },
  methods: {
    changePageTitle(hasClassroom: boolean): void {
      if (hasClassroom) {
        document.title = `Descomplica | ${capitalizeAllWords(this.classroom.name)}`;
      }
    },
    shouldShowLiveLessonTab() {
      return this.$route.params.category !== 'pos';
    },
    openRevision(revisionList: ListModel) {
      this.$router.push({
        name: 'revision',
        params: {
          revisionSlug: revisionList.slug,
          classroomSlug: this.$route.params.classroomSlug,
        },
      });
    },
    async handleIfUserCanSeeClassroom(hasClassroom: boolean) {
      if (!hasClassroom) {
        this.$router.push({
          name: 'NotFound',
        });
      }
    },
  },
});
